import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Shield, Scale, FileText, Info, Lock } from 'lucide-react';

const LegalPage = ({ title, content, type }) => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const getIcon = () => {
        switch (type) {
            case 'privacy': return <Shield className="w-12 h-12 text-cyan-400" />;
            case 'terms': return <Scale className="w-12 h-12 text-cyan-400" />;
            case 'cookies': return <FileText className="w-12 h-12 text-cyan-400" />;
            case 'imprint': return <Info className="w-12 h-12 text-cyan-400" />;
            case 'data': return <Lock className="w-12 h-12 text-cyan-400" />;
            default: return <FileText className="w-12 h-12 text-cyan-400" />;
        }
    };

    return (
        <div className="min-h-screen bg-slate-950 flex flex-col">
            <Navbar />
            <main className="flex-grow pt-32 pb-24">
                <div className="container mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="max-w-4xl mx-auto"
                    >
                        <div className="flex items-center gap-4 mb-8">
                            <div className="p-3 bg-slate-900 rounded-2xl border border-white/5 shadow-xl">
                                {getIcon()}
                            </div>
                            <h1 className="text-4xl md:text-5xl font-black text-white">{title}</h1>
                        </div>

                        <div className="prose prose-invert prose-cyan max-w-none text-slate-300 bg-slate-900/40 backdrop-blur-xl p-8 md:p-12 rounded-[40px] border border-white/5 shadow-2xl overflow-y-auto">
                            {content}
                        </div>
                    </motion.div>
                </div>
            </main>
            <Footer />
        </div>
    );
};

export default LegalPage;

// Specialized Content Components
export const PrivacyPolicy = () => (
    <LegalPage
        type="privacy"
        title="Privacy Policy"
        content={
            <div className="space-y-6">
                <section>
                    <h2 className="text-2xl font-bold text-white mb-4">1. Introduction</h2>
                    <p>At Polyglots Talent, we respect your privacy and are committed to protecting your personal data. This policy explains how we handle your data when you use our website and services.</p>
                </section>
                <section>
                    <h2 className="text-2xl font-bold text-white mb-4">2. Data We Collect</h2>
                    <p>We collect information you provide directly to us (e.g., name, email, resume) and information collected automatically through cookies and similar technologies.</p>
                </section>
                <section>
                    <h2 className="text-2xl font-bold text-white mb-4">3. How We Use Your Data</h2>
                    <p>We use your information to match you with potential employers, improve our services, and communicate with you about career opportunities.</p>
                </section>
                <section>
                    <h2 className="text-2xl font-bold text-white mb-4">4. Sharing Your Information</h2>
                    <p>We share your information with verified recruiting partners and employers in Germany and other countries only with your explicit consent or as required for the recruitment process.</p>
                </section>
            </div>
        }
    />
);

export const TermsOfService = () => (
    <LegalPage
        type="terms"
        title="Terms of Service"
        content={
            <div className="space-y-6">
                <section>
                    <h2 className="text-2xl font-bold text-white mb-4">1. Agreement to Terms</h2>
                    <p>By using Polyglots Talent, you agree to these terms. If you do not agree, please do not use our services.</p>
                </section>
                <section>
                    <h2 className="text-2xl font-bold text-white mb-4">2. Use of Services</h2>
                    <p>You must provide accurate information when creating an account. You are responsible for maintaining the security of your credentials.</p>
                </section>
                <section>
                    <h2 className="text-2xl font-bold text-white mb-4">3. Recruitment Process</h2>
                    <p>While we strive to find the best match for both candidates and employers, we do not guarantee employment. The final hiring decision rests with the prospective employer.</p>
                </section>
                <section>
                    <h2 className="text-2xl font-bold text-white mb-4">4. Intellectual Property</h2>
                    <p>All content on this platform is the property of Polyglots GmbH and is protected by copyright laws.</p>
                </section>
            </div>
        }
    />
);

export const CookiePolicy = () => (
    <LegalPage
        type="cookies"
        title="Cookie Policy"
        content={
            <div className="space-y-6">
                <section>
                    <h2 className="text-2xl font-bold text-white mb-4">1. What are Cookies?</h2>
                    <p>Cookies are small text files stored on your device that help us provide a better user experience and analyze how our site is used.</p>
                </section>
                <section>
                    <h2 className="text-2xl font-bold text-white mb-4">2. How We Use Them</h2>
                    <p>We use essential cookies for platform functionality and analytical cookies to understand site performance. You can manage your preferences through your browser settings.</p>
                </section>
            </div>
        }
    />
);

export const Imprint = () => (
    <LegalPage
        type="imprint"
        title="Imprint"
        content={
            <div className="space-y-6">
                <section>
                    <h2 className="text-2xl font-bold text-white mb-4">Company Information</h2>
                    <p><strong>Polyglots GmbH</strong></p>
                    <p>Marzahner Ch 199, Berlin 12681, Germany</p>
                    <p>Email: info@polyglotstalent.com</p>
                    <p>Phone: +49 176 59581516</p>
                    <p>Represented by: Management Board</p>
                </section>
            </div>
        }
    />
);

export const DataProtection = () => (
    <LegalPage
        type="data"
        title="Data Protection"
        content={
            <div className="space-y-6">
                <section>
                    <h2 className="text-2xl font-bold text-white mb-4">GDPR Compliance</h2>
                    <p>Polyglots Talent is fully compliant with the General Data Protection Regulation (GDPR). We implement strict technical and organizational measures to ensure the security and privacy of your data.</p>
                </section>
                <section>
                    <h2 className="text-2xl font-bold text-white mb-4">Your Rights</h2>
                    <p>You have the right to access, rectify, or delete your personal data. You can also object to processing or request data portability at any time.</p>
                </section>
            </div>
        }
    />
);
